/**
 * Comment Section — platform feed posts
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PortraitFrame } from '../common/editorial/EditorialControls';
import UserAvatar from '../common/UserAvatar';
import { CheckBadgeIcon } from '@heroicons/react/24/outline';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const CommentSection = ({ postId, user, onCommentAdded }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadComments();
  }, [postId]);

  const loadComments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/platform/posts/${postId}/comments`);
      setComments(response.data.data?.comments || []);
    } catch (err) {
      console.error('Error loading comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      await axios.post(`/api/platform/posts/${postId}/comments`, {
        content: newComment.trim(),
      });
      setNewComment('');
      loadComments();
      onCommentAdded();
    } catch (err) {
      alert('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const userInitials = (user?.full_name || user?.profile_name || 'U').charAt(0).toUpperCase();

  return (
    <div className="mt-5 pt-5 border-t border-hairline">
      <form onSubmit={handleSubmit} className="mb-5 flex gap-3">
        <UserAvatar
          userId={user?.user_id}
          src={user?.avatar_url}
          alt={user?.full_name || 'You'}
          size="xs"
        />
        <div className="flex-1 flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write a comment…"
            className="input-editorial flex-1"
          />
          <button
            type="submit"
            disabled={submitting || !newComment.trim()}
            className="btn-primary shrink-0"
          >
            Post
          </button>
        </div>
      </form>

      {loading ? (
        <p className="text-center text-label text-secondary py-4">Loading comments…</p>
      ) : comments.length === 0 ? (
        <p className="text-center text-label text-secondary py-4">No comments yet</p>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.comment_id} className="flex gap-3">
              {comment.author?.is_verified_advisor ? (
                <PortraitFrame
                  size="xs"
                  src={comment.author?.avatar_url}
                  alt={comment.author?.name}
                  initials={comment.author?.name?.charAt(0)?.toUpperCase() || 'U'}
                />
              ) : (
                <UserAvatar
                  userId={comment.author?.user_id}
                  src={comment.author?.avatar_url}
                  alt={comment.author?.name}
                  size="xs"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="rounded-lg border border-hairline bg-bone p-3">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-label font-medium text-primary">{comment.author?.name}</span>
                    {comment.author?.verified && (
                      <CheckBadgeIcon className="w-3.5 h-3.5 text-pine" />
                    )}
                    <span className="text-label text-faint">{formatDate(comment.created_at)}</span>
                  </div>
                  <p className="text-body text-primary">{comment.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentSection;
